import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
    Grid,
    FormControl,
    TextField,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Typography,
    Box,
    Select,
    MenuItem,
    InputLabel,
} from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import ListIcon from '@mui/icons-material/List';
import { AkbankTransactionLogsDrawer } from './components/AkbankTransactionLogsDrawer';

import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear, SCUIDateRanger } from '../../atomic/atoms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { RefundRestQuery } from '../../services/query';
import Helper from '../../utils/helper';
import TimeHelper from '../../utils/time';

import cashPayment from '../../assets/icons/svg/pageIcons/cashPayment.svg';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';

const formatTransactionDate = (dateValue: any): string => {
    if (!dateValue) return '-';
    try {
        if (typeof dateValue !== 'string') {
            return TimeHelper.formatDateHourSecond(dateValue);
        }

        if (dateValue.includes('T') || dateValue.includes('Z') || dateValue.includes('+')) {
            const formatted = TimeHelper.formatDateHourWithTimezone(dateValue);
            if (formatted) return formatted;
        }

        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
            return TimeHelper.formatDateHourSecond(date);
        }

        return dateValue;
    } catch {
        return dateValue || '-';
    }
};

const mapTransactionItem = (item: any, index: number) => ({
    id: item.transactionId || index,
    transactionId: item.transactionId,
    transactionDate: formatTransactionDate(item.transactionDate),
    customerId: item.customerId,
    refundId: item.refundId ?? item.RefundId ?? '-',
    amountRefunded: item.amountRefunded ?? item.AmountRefunded ?? '-',
    refundIbanName: item.refundIbanName ?? item.RefundIbanName ?? '-',
    orderNumber: item.orderNumber || '-',
    createdDate: formatTransactionDate(item.createdDate),
    lastQueryDate: formatTransactionDate(item.lastQueryDate),
    queryAttemptCount: item.queryAttemptCount,
    isCompleted: item.isCompleted,
    errorMessage: item.errorMessage || '-',
    requestResponseLogsCount: item.requestResponseLogs?.length || 0,
    requestResponseLogs: item.requestResponseLogs || [],
});

interface UrlSearchParamsState {
    customerIdParameter: string | null;
    transactionIdParameter: string | null;
    refundIdParameter: string | null;
    startDateParameter: string | null;
    endDateParameter: string | null;
    isCompletedParameter: string | null;
    onlyFailedParameter: string | null;
}

interface ApplyUrlSearchParamsCallbacks {
    setSearchType: (value: string) => void;
    setSearchValue: (value: string) => void;
    setStartDate: (value: string) => void;
    setEndDate: (value: string) => void;
    setIsCompleted: (value: boolean) => void;
    setOnlyFailed: (value: boolean) => void;
}

const hasAnyUrlSearchParam = (params: UrlSearchParamsState): boolean =>
    Boolean(
        params.customerIdParameter ||
            params.transactionIdParameter ||
            params.refundIdParameter ||
            params.startDateParameter ||
            params.endDateParameter ||
            params.isCompletedParameter !== null ||
            params.onlyFailedParameter !== null
    );

const applySearchIdentityFromUrl = (
    req: any,
    params: UrlSearchParamsState,
    callbacks: ApplyUrlSearchParamsCallbacks
) => {
    if (params.customerIdParameter) {
        req.CustomerId = Number(params.customerIdParameter);
        callbacks.setSearchType('CustomerId');
        callbacks.setSearchValue(params.customerIdParameter);
        return;
    }

    if (params.transactionIdParameter) {
        req.TransactionId = params.transactionIdParameter;
        callbacks.setSearchType('TransactionId');
        callbacks.setSearchValue(params.transactionIdParameter);
        return;
    }

    if (params.refundIdParameter) {
        req.RefundId = Number(params.refundIdParameter);
        callbacks.setSearchType('RefundId');
        callbacks.setSearchValue(params.refundIdParameter);
    }
};

const applyDateFiltersFromUrl = (
    req: any,
    params: UrlSearchParamsState,
    callbacks: ApplyUrlSearchParamsCallbacks
) => {
    if (params.startDateParameter) {
        req.StartDate = params.startDateParameter;
        callbacks.setStartDate(params.startDateParameter);
    }
    if (params.endDateParameter) {
        req.EndDate = params.endDateParameter;
        callbacks.setEndDate(params.endDateParameter);
    }
};

const applyBooleanFiltersFromUrl = (
    req: any,
    params: UrlSearchParamsState,
    callbacks: ApplyUrlSearchParamsCallbacks
) => {
    if (params.isCompletedParameter !== null) {
        req.IsCompleted = params.isCompletedParameter === 'true';
        callbacks.setIsCompleted(params.isCompletedParameter === 'true');
    } else {
        callbacks.setIsCompleted(false);
    }

    if (params.onlyFailedParameter !== null) {
        req.OnlyFailed = params.onlyFailedParameter === 'true';
        callbacks.setOnlyFailed(params.onlyFailedParameter === 'true');
    } else {
        callbacks.setOnlyFailed(false);
    }
};

const applyUrlSearchParamsToRequest = (
    req: any,
    params: UrlSearchParamsState,
    shouldHydrateFilters: boolean,
    callbacks: ApplyUrlSearchParamsCallbacks
) => {
    if (!hasAnyUrlSearchParam(params) || !shouldHydrateFilters) {
        return;
    }

    applySearchIdentityFromUrl(req, params, callbacks);
    applyDateFiltersFromUrl(req, params, callbacks);
    applyBooleanFiltersFromUrl(req, params, callbacks);
};

export const AkbankTransactionStatusList: FC<any> = () => {
    const [transactionData, setTransactionData] = useState<any[] | null>([]);
    const [summaryData, setSummaryData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState('CustomerId');
    const [searchValue, setSearchValue] = useState('');
    const [startDate, setStartDate] = useState(moment().subtract(14, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().add(1, 'days').format('YYYY-MM-DD'));
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [onlyFailed, setOnlyFailed] = useState<boolean>(false);
    const [excelDatas, setExcelDatas] = useState<any>();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedLogs, setSelectedLogs] = useState<any[]>([]);

    const location = useLocation();
    const search = location.search;
    const searchParams = new URLSearchParams(search);
    const { t } = useTranslation();
    const history = useHistory();

    const _handlChangeDate = (event: any) => {
        setStartDate(moment(event[0], 'DD/MM/YYYY').format('YYYY-MM-DD'));
        setEndDate(moment(event[1], 'DD/MM/YYYY').format('YYYY-MM-DD'));
    };

    useEffect(() => {
        if (transactionData) {
            buildExcelDatas(transactionData);
        }
    }, [transactionData]);

    let req: any = {};
    let customerIdParameter = searchParams.get('CustomerId') || searchParams.get('customerId');
    let transactionIdParameter = searchParams.get('TransactionId') || searchParams.get('transactionId');
    let refundIdParameter = searchParams.get('RefundId') || searchParams.get('refundId');
    let startDateParameter = searchParams.get('StartDate') || searchParams.get('startDate');
    let endDateParameter = searchParams.get('EndDate') || searchParams.get('endDate');
    let isCompletedParameter = searchParams.get('IsCompleted');
    let onlyFailedParameter = searchParams.get('OnlyFailed');

    useEffect(() => {
        if (search.length === 0) {
            setTransactionData(null);
            return;
        }

        const urlParams: UrlSearchParamsState = {
            customerIdParameter,
            transactionIdParameter,
            refundIdParameter,
            startDateParameter,
            endDateParameter,
            isCompletedParameter,
            onlyFailedParameter,
        };

        applyUrlSearchParamsToRequest(req, urlParams, !transactionData || transactionData.length === 0, {
            setSearchType,
            setSearchValue,
            setStartDate,
            setEndDate,
            setIsCompleted,
            setOnlyFailed,
        });

        listele(req);
    }, [
        customerIdParameter,
        transactionIdParameter,
        refundIdParameter,
        startDateParameter,
        endDateParameter,
        isCompletedParameter,
        onlyFailedParameter,
    ]);

    const CallTransactionList = (params: any) => {
        setLoading(true);
        RefundRestQuery.GetAkbankTransactionStatusList(params)
            .then((res: any) => {
                if (res?.transactions && res.transactions.length > 0) {
                    setTransactionData(res.transactions.map(mapTransactionItem));
                    setSummaryData({
                        totalCount: res.totalCount || 0,
                        completedCount: res.completedCount || 0,
                        pendingCount: res.pendingCount || 0,
                        failedCount: res.failedCount || 0,
                    });
                    return;
                }

                setTransactionData([]);
                setSummaryData(null);
                if (res?.transactions?.length === 0) {
                    Swal.fire(t('akbankTransactionStatusList.noTransactionFound'), '', 'info');
                }
            })
            .catch((error: any) => {
                Swal.fire('Error', error.toString(), 'error');
                setTransactionData([]);
                setSummaryData(null);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const defaultColumnOptions = {
        filterable: false,
        disableReorder: false,
        sortable: false,
        hideable: false,
        editable: false,
        resizable: false,
        groupable: false,
        disableColumnMenu: true,
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'transactionId',
            headerName: t('akbankTransactionStatusList.transactionId'),
            flex: 1,
            minWidth: 150,
            ...defaultColumnOptions,
        },
        {
            field: 'transactionDate',
            headerName: t('akbankTransactionStatusList.transactionDate'),
            flex: 1,
            minWidth: 150,
            ...defaultColumnOptions,
        },
        {
            field: 'customerId',
            headerName: t('akbankTransactionStatusList.customerId'),
            flex: 1,
            minWidth: 120,
            ...defaultColumnOptions,
        },
        {
            field: 'refundId',
            headerName: t('akbankTransactionStatusList.refundId'),
            flex: 1,
            minWidth: 120,
            ...defaultColumnOptions,
        },
        {
            field: 'amountRefunded',
            headerName: t('akbankTransactionStatusList.amountRefunded'),
            flex: 1,
            minWidth: 120,
            ...defaultColumnOptions,
        },
        {
            field: 'refundIbanName',
            headerName: t('akbankTransactionStatusList.refundIbanName'),
            flex: 1,
            minWidth: 150,
            ...defaultColumnOptions,
        },
        {
            field: 'orderNumber',
            headerName: t('akbankTransactionStatusList.orderNumber'),
            flex: 1,
            minWidth: 120,
            ...defaultColumnOptions,
        },
        {
            field: 'createdDate',
            headerName: t('akbankTransactionStatusList.createdDate'),
            flex: 1,
            minWidth: 150,
            ...defaultColumnOptions,
        },
        {
            field: 'lastQueryDate',
            headerName: t('akbankTransactionStatusList.lastQueryDate'),
            flex: 1,
            minWidth: 150,
            renderCell: (params: any) => {
                return (
                    <Tooltip title={params.value || '-'}>
                        <div>{params.value || '-'}</div>
                    </Tooltip>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'queryAttemptCount',
            headerName: t('akbankTransactionStatusList.queryAttemptCount'),
            flex: 1,
            minWidth: 120,
            ...defaultColumnOptions,
        },
        {
            field: 'isCompleted',
            headerName: t('akbankTransactionStatusList.isCompleted'),
            flex: 1,
            minWidth: 120,
            renderCell: (params: any) => {
                return (
                    <div style={{ color: params.value ? 'green' : 'red' }}>
                        {params.value ? t('general.completed') : t('general.failed')}
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'errorMessage',
            headerName: t('akbankTransactionStatusList.errorMessage'),
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                return (
                    <Tooltip title={params.value || '-'}>
                        <div style={{ color: params.value ? 'red' : 'inherit' }}>{params.value || '-'}</div>
                    </Tooltip>
                );
            },
            ...defaultColumnOptions,
        },
        {
            field: 'requestResponseLogsCount',
            headerName: t('akbankTransactionStatusList.requestResponseLogsCount'),
            flex: 1,
            minWidth: 150,
            renderCell: (params: any) => {
                const count = params.row.requestResponseLogsCount || 0;
                return (
                    <div
                        className="akbank-transaction-status-list-open-logs"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: count > 0 ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                            if (count > 0) {
                                setSelectedLogs(params.row.requestResponseLogs || []);
                                setDrawerOpen(true);
                            }
                        }}
                    >
                        <ListIcon
                            sx={{
                                fontSize: '18px',
                                color: count > 0 ? '#1976d2' : '#9e9e9e',
                            }}
                        />
                        <span
                            style={{
                                color: count > 0 ? '#1976d2' : 'inherit',
                                textDecoration: count > 0 ? 'underline' : 'none',
                            }}
                        >
                            {count}
                        </span>
                    </div>
                );
            },
            ...defaultColumnOptions,
        },
    ];

    const listele = async (request?: any) => {
        if (request !== null && Object.keys(request).length > 0) {
            CallTransactionList(request);
        } else {
            if (startDate.trim().length === 0 || endDate.trim().length === 0) {
                Swal.fire(t('akbankTransactionStatusList.selectDate'), '', 'error');
            } else {
                setLoading(true);
                try {
                    let params: any = BuildRequest(searchType, searchValue, startDate, endDate, isCompleted, onlyFailed);
                    CallTransactionList(params);
                    setLoading(false);
                } catch (error: any) {
                    Swal.fire('Error', error.toString(), 'error');
                }
            }
        }
    };

    const formatJSON = (jsonString: string) => {
        try {
            const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
            return JSON.stringify(parsed, null, 2);
        } catch {
            return jsonString;
        }
    };

    const buildExcelDatas = (transactionData_: any[]) => {
        const downloadedData: any[] = [];

        transactionData_.forEach((item: any) => {
            const baseData = {
                transactionId: item.transactionId || '-',
                transactionDate: item.transactionDate || '-',
                customerId: item.customerId || '-',
                refundId: item.refundId || '-',
                amountRefunded: item.amountRefunded || '-',
                refundIbanName: item.refundIbanName || '-',
                orderNumber: item.orderNumber && item.orderNumber !== '-' ? item.orderNumber : '-',
                createdDate: item.createdDate || '-',
                lastQueryDate: item.lastQueryDate || '-',
                queryAttemptCount: item.queryAttemptCount || 0,
                isCompleted: item.isCompleted ? t('general.completed') : t('general.waitingForApproval'),
                errorMessage: item.errorMessage || '-',
                requestResponseLogsCount: item.requestResponseLogsCount || 0,
            };

            if (item.requestResponseLogs && item.requestResponseLogs.length > 0) {
                // Her log için ayrı satır oluştur
                item.requestResponseLogs.forEach((log: any, index: number) => {
                    let requestData = '-';
                    let responseData = '-';

                    if (log.requestData) {
                        try {
                            requestData = formatJSON(log.requestData);
                        } catch {
                            requestData = log.requestData || '-';
                        }
                    }

                    if (log.responseData) {
                        try {
                            responseData = formatJSON(log.responseData);
                        } catch {
                            responseData = log.responseData || '-';
                        }
                    }

                    downloadedData.push({
                        ...baseData,
                        logNumber: index + 1,
                        requestData: requestData,
                        responseData: responseData,
                    });
                });
            } else {
                // Log yoksa tek satır ekle
                downloadedData.push({
                    ...baseData,
                    logNumber: '-',
                    requestData: '-',
                    responseData: '-',
                });
            }
        });

        setExcelDatas(downloadedData);
    };

    return (
        <SCUIContent>
            <AkbankTransactionLogsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} logs={selectedLogs} />
            <Grid
                className="akbank-transaction-status-list"
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <Grid
                    className="akbank-transaction-status-list-header"
                    direction="row"
                    spacing={2}
                    sx={{ marginLeft: '0.5rem' }}
                >
                    <SCUIBreadCrumb
                        title={t('akbankTransactionStatusList.title')}
                        componentLink={'/akbank-transaction-status-list'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setTransactionData([]);
                            setSearchValue('');
                            setSearchType('CustomerId');
                            setEndDate(moment().add(1, 'days').format('YYYY-MM-DD'));
                            setStartDate(moment().subtract(14, 'days').format('YYYY-MM-DD'));
                            setIsCompleted(false);
                            setOnlyFailed(false);
                        }}
                    />
                    <SCUILoadingLinear display={loading} />
                </Grid>
                <Grid
                    className="akbank-transaction-status-list-main"
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <Grid
                        size={{
                            sm: 6,
                            md: 6,
                            lg: 6,
                            xl: 6,
                        }}
                    >
                        {transactionData && transactionData.length > 0 ? (
                            <Grid
                                className="akbank-transaction-status-list-results"
                                size={{
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                }}
                            >
                                {summaryData && (
                                    <Box
                                        className="akbank-transaction-status-list-summary"
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            marginBottom: 2,
                                            padding: 2,
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="body1">
                                            <strong>{t('akbankTransactionStatusList.totalCount')}:</strong>{' '}
                                            {summaryData.totalCount}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'green' }}>
                                            <strong>{t('akbankTransactionStatusList.completedCount')}:</strong>{' '}
                                            {summaryData.completedCount}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'orange' }}>
                                            <strong>{t('akbankTransactionStatusList.pendingCount')}:</strong>{' '}
                                            {summaryData.pendingCount}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'red' }}>
                                            <strong>{t('akbankTransactionStatusList.failedCount')}:</strong>{' '}
                                            {summaryData.failedCount}
                                        </Typography>
                                    </Box>
                                )}
                                <Box className="akbank-transaction-status-list-excel-download">
                                    <SCUIDownloadButton
                                        position="right"
                                        excelData={{
                                            excelDownloadData: excelDatas,
                                            hiddenExcelColumn: ['id'],
                                            excelDownloadFolderName: t('excel.akbankTransactionStatusList'),
                                        }}
                                    />
                                </Box>
                                <Box className="akbank-transaction-status-list-data-grid" sx={{ width: '100%' }}>
                                    <SCUICustomDataGrid
                                        sx={{
                                            '& .MuiDataGrid-cell': {
                                                fontSize: '8px',
                                            },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontSize: '8px',
                                            },
                                        }}
                                        randomId
                                        rows={transactionData}
                                        columns={columns}
                                        excelDownloadButton={false}
                                        excelDownloadButtonName={t('general.exportToExcel')}
                                        excelDownloadData={excelDatas}
                                        excelDownloadFolderName={'AkbankTransactionStatusList'}
                                    />
                                </Box>
                            </Grid>
                        ) : (
                            <div
                                className="akbank-transaction-status-list-empty-state"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignContent: 'center',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <SCUINoRecordComponent
                                    text={t('akbankTransactionStatusList.makeSearch')}
                                    height={'140px'}
                                    width={'140px'}
                                    fontSize="12px"
                                    img={cashPayment}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: 450,
                                        marginTop: '0.5rem',
                                        marginBottom: '1rem',
                                        gap: 1,
                                    }}
                                >
                                    <FormControl sx={{ flex: 1 }}>
                                        <InputLabel id="search-type-label">{t('akbankTransactionStatusList.searchType')}</InputLabel>
                                        <Select
                                            labelId="search-type-label"
                                            value={searchType}
                                            label={t('akbankTransactionStatusList.searchType')}
                                            onChange={(e) => {
                                                setSearchType(e.target.value);
                                                setSearchValue('');
                                            }}
                                        >
                                            <MenuItem value="CustomerId">{t('akbankTransactionStatusList.customerId')}</MenuItem>
                                            <MenuItem value="TransactionId">{t('akbankTransactionStatusList.transactionId')}</MenuItem>
                                            <MenuItem value="RefundId">{t('akbankTransactionStatusList.refundId')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ flex: 2 }}>
                                        <TextField
                                            label={
                                                searchType === 'CustomerId'
                                                    ? t('akbankTransactionStatusList.customerId')
                                                    : searchType === 'TransactionId'
                                                    ? t('akbankTransactionStatusList.transactionId')
                                                    : t('akbankTransactionStatusList.refundId')
                                            }
                                            placeholder={
                                                searchType === 'CustomerId'
                                                    ? t('akbankTransactionStatusList.enterCustomerId')
                                                    : searchType === 'TransactionId'
                                                    ? t('akbankTransactionStatusList.enterTransactionId')
                                                    : t('akbankTransactionStatusList.enterRefundId')
                                            }
                                            value={searchValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (searchType === 'CustomerId' || searchType === 'RefundId') {
                                                    // Sadece rakam
                                                    if (/^\d*$/.test(value)) {
                                                        setSearchValue(value);
                                                    }
                                                } else {
                                                    // Alfanumerik
                                                    if (/^[a-zA-Z0-9]*$/.test(value)) {
                                                        setSearchValue(value);
                                                    }
                                                }
                                            }}
                                            type={searchType === 'CustomerId' || searchType === 'RefundId' ? 'text' : 'text'}
                                        />
                                    </FormControl>
                                </Box>
                                <Grid
                                    className="akbank-transaction-status-list-date-range"
                                    sx={{ marginBottom: 1, width: '450px !important', marginTop: '5px' }}
                                >
                                    <SCUIDateRanger
                                        fromDate={moment().subtract(14, 'days').format('DD/MM/YYYY')}
                                        toDate={moment().add(1, 'days').format('DD/MM/YYYY')}
                                        setDateRange={_handlChangeDate}
                                        dateFormat="DD/MM/YYYY"
                                        fullWidth
                                        containerStyling={{ width: '100%' }}
                                    />
                                </Grid>
                                <Box sx={{ display: 'flex', width: 450, marginTop: '5px', gap: 2 }}>
                                    <FormControl className="akbank-transaction-status-list-is-completed-field">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    className="akbank-transaction-status-list-is-completed-checkbox"
                                                    checked={isCompleted}
                                                    onChange={(e) => {
                                                        setIsCompleted(e.target.checked);
                                                    }}
                                                />
                                            }
                                            label={t('akbankTransactionStatusList.isCompleted')}
                                        />
                                    </FormControl>
                                    <FormControl className="akbank-transaction-status-list-only-failed-field">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    className="akbank-transaction-status-list-only-failed-checkbox"
                                                    checked={onlyFailed}
                                                    onChange={(e) => {
                                                        setOnlyFailed(e.target.checked);
                                                    }}
                                                />
                                            }
                                            label={t('akbankTransactionStatusList.onlyFailed')}
                                        />
                                    </FormControl>
                                </Box>
                                <FormControl
                                    className="akbank-transaction-status-list-filter-field"
                                    style={{ width: 450 }}
                                >
                                    <SCUIButton
                                        className="akbank-transaction-status-list-filter-button"
                                        text={t('general.filter')}
                                        type="primary"
                                        sizeType="default"
                                        size={'l'}
                                        onClick={() => {
                                            if ((!searchValue || searchValue.trim() === '') && !isCompleted && !onlyFailed) {
                                                Swal.fire(
                                                    t('general.error') || 'Hata',
                                                    t('akbankTransactionStatusList.pleaseEnterSearchValue'),
                                                    'error'
                                                );
                                                return;
                                            }
                                            history.push(
                                                '/akbank-transaction-status-list?' +
                                                    Helper.buildURLFromObject(
                                                        BuildRequest(
                                                            searchType,
                                                            searchValue,
                                                            startDate,
                                                            endDate,
                                                            isCompleted,
                                                            onlyFailed
                                                        )
                                                    )
                                            );
                                        }}
                                    />
                                </FormControl>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};

function BuildRequest(
    searchType: string,
    searchValue: string,
    startDate: string,
    endDate: string,
    isCompleted: boolean,
    onlyFailed: boolean
) {
    let params: any = {};
    if (searchValue) {
        if (searchType === 'CustomerId') {
            params.CustomerId = Number(searchValue);
        } else if (searchType === 'TransactionId') {
            params.TransactionId = searchValue;
        } else if (searchType === 'RefundId') {
            params.RefundId = Number(searchValue);
        }
    }
    if (startDate) {
        params.StartDate = startDate;
    }
    if (endDate) {
        params.EndDate = endDate;
    }
    if (isCompleted) {
        params.IsCompleted = isCompleted;
    }
    if (onlyFailed) {
        params.OnlyFailed = onlyFailed;
    }
    return params;
}
